import React, { useEffect, useMemo, useState } from 'react';
import LeafletMap from '../../components/LeafletMap';
import {
	Button,
	DownloadButton,
} from '../../components/StyledComponents';
import { StatusLabel } from '../../components/ui/StatusLabel';
import DateFormat from '../../lib.r2/utils/DateFormat';
import Store from '../../store/Store';
import { Body } from '../../components/layout/Body';
import { useParams } from 'react-router-dom';
import { IconInfoBlue, DangerIcon, IconCloseOrange } from '../../components/Icons';
import { Loading } from '../../components/ui/Loading';
import { Page } from '../../components/ui/Page'
import { useDialog } from '../../hooks/useDialog';
import FlightDetailsSection from '../../components/features/FlightDetailsSection';
export interface OrderPageConsumerProps {
	id?: string;
}

interface SelectedOrder {
	orderId: string;
	name: string;
	orderState: string;
	submitted?: string;
	dateFrom: string;
	dateTo: string;
	coordinates: Record<string, unknown>;
	stripeHostedInvoiceUrl?: string;
	stripeInvoicePdfUrl?: string;
	flightHubTaskBeginAt?: string | null;
	droneModelName?: string | null;
	droneSerialNumber?: string | null;
	dockModelName?: string | null;
	dockSerialNumber?: string | null;
	gsdCm?: number | null;
	coordinateSystem?: string | null;
	geoidModel?: string | null;
	calibratedImages?: string | null;
	imageCount?: number | null;
	sensorModel?: string | null;
}


export default function OrderPageConsumer({ id }: OrderPageConsumerProps) {
	const { showConfirmDialog, showMessageDialog } = useDialog();
	const { orderId } = useParams();
	const [action, setAction] = useState('');
	const [reloadOrder, setReloadOrder] = useState(false);
	const [feedbackOpen, setFeedbackOpen] = useState(false);
	const [feedbackRating, setFeedbackRating] = useState(0);
	const [feedbackText, setFeedbackText] = useState('');
	const [feedbackSending, setFeedbackSending] = useState(false);

	const [selectedOrder, orderResults] = Store.use(
		'selectedOrder',
		'orderResults'
	) as [SelectedOrder | undefined, unknown];
	const [orderState, setOrderState] = useState(selectedOrder?.orderState ?? '');

	const mapSetup = useMemo(() => ({}), []);

	// Sync local orderState with store
	useEffect(() => {
		if (selectedOrder?.orderState != null) setOrderState(selectedOrder.orderState);
	}, [selectedOrder?.orderState]);

	// Reload order after cancellation or when requested
	useEffect(() => {
		if (reloadOrder && orderId) {
			Store.selectOrderById(orderId);
			setReloadOrder(false);
			setAction(''); // reset action so UI is fresh
		}
	}, [reloadOrder, orderId]);

	// Initial load if needed
	useEffect(() => {
		if (orderId && selectedOrder?.orderId !== orderId) {
			Store.selectOrderById(orderId);
		}
	}, [orderId, selectedOrder?.orderId]);

	// Prevent crash: if selectedOrder is not loaded yet, do not render or access its properties
	if (!selectedOrder) return <Loading spinner title="Lade Auftrag..." />;

	// --- Extract mission and group result types by purpose from orderResultTypes ---
	// Define the type for orderResultTypes if not present in SelectedOrder
	type OrderResultType = {
		resultType: {
			missionName: string;
			purposeName: string;
			name: string;
		};
	};

	// Use orderResultTypes from selectedOrder if available, otherwise use an empty array
	const orderResultTypes: OrderResultType[] = (selectedOrder as any).orderResultTypes || [];

	// Mission is always the same for all result types, so take the first one
	const mission = orderResultTypes[0]?.resultType?.missionName || '';

	// Group result types by purpose
	const purposesMap: Record<string, string[]> = {};
	orderResultTypes.forEach((rt: OrderResultType) => {
		const purpose = rt.resultType.purposeName;
		const typeName = rt.resultType.name;
		if (!purposesMap[purpose]) {
			purposesMap[purpose] = [];
		}
		purposesMap[purpose].push(typeName);
	});

	// Downloadable result files: everything that has a resultType (the internal
	// polygon .kmz has resultType null and is excluded).
	const downloadableResults = ((orderResults as any[]) ?? []).filter(
		(file) => file?.downloadUrl && file?.resultType
	);

	// DEMO: "Modell anzeigen" link, controlled per order via localStorage.
	// Set it with:
	//   localStorage.setItem('pix4dModelLinks', JSON.stringify({ '<orderId>': '<url>' }))
	// If no link is stored for this order, the button is not rendered.
	let modelLink = '';
	try {
		const raw = localStorage.getItem('pix4dModelLinks');
		if (raw) {
			const map = JSON.parse(raw) as Record<string, string>;
			modelLink = map?.[selectedOrder.orderId] ?? '';
		}
	} catch {
		modelLink = '';
	}

	console.log('selectedOrder:', selectedOrder);

	if (action === 'CANCELED')
		return (
			<Page center>
				<Body center>
					<h1>Stornierung erfolgreich!</h1>
					<h3>
						Alles erledigt, Sie sind fertig. Sie haben die Bestellung storniert
						und erhalten in Kürze eine Rückerstattung.
					</h3>
					<div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 24 }}>
						<Button
							data-pw="button-cancel-success"
							primary
							onClick={() => Store.navigate('dashboard')}
						>
							Fertig
						</Button>
						{selectedOrder.stripeInvoicePdfUrl && (
							<DownloadButton
								url={selectedOrder.stripeInvoicePdfUrl}
								label="Daten abrufen"
								self
							/>
						)}
					</div>
				</Body>
			</Page>
		);

	const onCancelOrder = async () => {
		showConfirmDialog({
			title: 'Auftrag stornieren?',
			text: 'Sind Sie sicher, dass Sie den Auftrag stornieren wollen?',
			icon: <DangerIcon />,
			confirmLabel: 'Ja, stornieren',
			onConfirm: async () => {
				const result = await (Store.removeOrder as (order: unknown, role: string) => Promise<unknown>)(selectedOrder, 'consumer');
				if (result) {
					showMessageDialog({
						title: 'Der Auftrag wurde storniert!',
						text: 'Sie haben die Bestellung storniert und erhalten in Kürze eine Rückerstattung.',
						onClose: () => Store.navigate('dashboard'),
					});
				} else {
					Store.setPopupMessage(
						'error',
						'Autrag stornieren',
						'Fehler beim Stornieren das Auftrages!'
					);
				}
			},
		});
	};

	const closeFeedback = () => {
		setFeedbackOpen(false);
		setFeedbackRating(0);
		setFeedbackText('');
	};

	// Feedback is mailed via the existing contact endpoint
	const onSendFeedback = async () => {
		const user = Store.user;
		setFeedbackSending(true);
		try {
			const result = await Store.fetch('contact', {
				method: 'POST',
				body: JSON.stringify({
					name: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || '',
					email: user?.email ?? '',
					subject: `Feedback zur Bestellung ${selectedOrder.orderId} (${selectedOrder.name})`,
					message: `Bewertung: ${feedbackRating > 0 ? `${feedbackRating}/5` : '-'}\n\n${feedbackText.trim()}`,
				}),
			});
			if (!result) throw new Error('feedback failed');
			closeFeedback();
			showMessageDialog({
				title: 'Vielen Dank für Ihr Feedback!',
				text: 'Ihre Rückmeldung wurde an unser Team übermittelt.',
			});
		} catch {
			Store.setPopupMessage('error', 'Feedback', 'Feedback konnte nicht gesendet werden.');
		} finally {
			setFeedbackSending(false);
		}
	};

	// Info messages for each order state
	const orderStateInfo: Record<string, string[]> = {
		CANCELED: [
			'Ihr Auftrag wurde storniert.',
			'Erforderliche behördliche Genehmigungen konnten nicht erteilt werden.',
		],
		COMPLETED: [
			'Der Auftrag wurde erfolgreich durchgeführt.',
			'Die Auswertung steht zum Download bereit.'
		],
		ONGOING: [
			'Ihr Auftrag wurde freigegeben und befindet sich nun in Bearbeitung.'
		],
		DELAYED: [
			'Der Auftrag verzögert sich aufgrund äußerer Umstände.',
			'Wir informieren Sie, sobald sich der Status Ihres Auftrags ändert.'
		],
		AWAITING_APPROVAL: [
			'Ihr Auftrag wurde erfolgreich übermittelt und wird derzeit geprüft.'
		],
		AWAITING_PAYMENT: [
			'Die Zahlung für diesen Auftrag ist noch ausständig. Bitte schließen Sie den Zahlungsvorgang ab, um die Bearbeitung fortzusetzen.'
		]
	};

	return (
		<div className="max-w-[1300px] m-auto p-[25px] md:px-[20px] md:py-[20px]">
			<div className="order-page flex gap-5 justify-between">
				<div className="order-page-left">
					<div className='border-b border-[#333] pb-8 mb-8'>
						<div className="flex items-center gap-3 flex-wrap justify-between mb-5">
							<div className="flex h-full items-center">
								<h1 className="text-2xl font-bold max-w-[350px] break-words whitespace-normal">{selectedOrder.name}</h1>
							</div>
							<StatusLabel>{orderState as 'CANCELED' | 'COMPLETED' | 'DELAYED' | 'AWAITING_PAYMENT' | 'AWAITING_APPROVAL' | 'ONGOING'}</StatusLabel>
						</div>
						<span className="text-[#666666]">
							Erstellt am: {selectedOrder.submitted ? DateFormat(selectedOrder.submitted) : ''}
						</span>
						<br />

						<span className="ml-auto text-[#666666]">Auftragsnummer: {selectedOrder.orderId}</span>
						<div className='flex items-top gap-1 mt-4 mb-6'>
							<IconInfoBlue />
							<div>
								{orderStateInfo[orderState]?.map((msg, idx) => (
									<p key={idx} className='text-[#0063A3] ml-2'>{msg}</p>
								))}
							</div>
						</div>

					</div>



					<h2 className='text-2xl mb-4 font-normal'>Buchungsdetails</h2>
					<div className='mb-4'>
						<h3 className='text-base font-semibold p-0 m-0'>Anwendungsfall</h3>
						<div className='text-base'>{mission}</div>
					</div>
					<div className='mb-4'>
						<h3 className='text-base font-semibold p-0 m-0'>Dateiformate</h3>
						<div>
							{Object.entries(purposesMap).map(([purpose, types]) => (
								<div className='text-base' key={purpose}>
									{purpose}: {types.join(', ')}
								</div>
							))}
						</div>
					</div>
					<div className='mb-4'>
						<h3 className='text-base font-semibold p-0 m-0'>Gewünschter Befliegungszeitraum</h3>
						<div className='text-base'>
							{DateFormat(selectedOrder.dateFrom, 'dd.mm.yyyy') +
								' - ' +
								DateFormat(selectedOrder.dateTo, 'dd.mm.yyyy')}
						</div>
					</div>
					<FlightDetailsSection order={selectedOrder} />

					{/* Show cancellation conditions only if cancel button is visible */}
					{!(orderState === 'ONGOING' || orderState === 'CANCELED' || orderState === 'COMPLETED') && (
						<div>
							<h3 className='text-base font-semibold p-0 m-0'>Stornierungsbedingungen</h3>
							<span className='text-base'>
								Sie können den Auftrag stornieren, wenn Sie Ihre Meinung ändern
								oder die Angaben falsch sind
							</span>
						</div>
					)}

					{(downloadableResults.length > 0 || modelLink) && (
						<div className='mt-8 pt-6 border-t border-[#333]'>
							<h2 className='text-2xl mb-4 font-normal'>Ergebnisse</h2>
							{modelLink && (
								<div className="max-w-[200px] mb-4">
									<Button
										data-pw="button-view-model"
										primary
										onClick={() => window.open(modelLink, '_blank', 'noopener,noreferrer')}
									>
										Modell anzeigen
									</Button>
								</div>
							)}
							<div className="flex flex-col gap-2 max-w-[400px]">
								{downloadableResults.map((file, index) => (
									<DownloadButton
										key={`${file.fileName ?? 'result'}-${index}`}
										label={String(file.fileName ?? 'Datei herunterladen')}
										url={String(file.downloadUrl)}
										self
									/>
								))}
							</div>
						</div>
					)}

					{selectedOrder.stripeHostedInvoiceUrl && (
						<div className='mt-8 pt-6 border-t border-[#333]'>
							<div>
								<h2 className='text-2xl mb-4 font-normal'>Downloads</h2>
							</div>
							<DownloadButton
								url={selectedOrder.stripeInvoicePdfUrl}
								label="Quittung abrufen"
								self
							/>
						</div>
					)}
					<div className="flex flex-row gap-4 max-w-[400px] mt-8">
						{/* Show 'Auftrag stornieren' except for ONGOING, CANCELED, COMPLETED */}
						{!(orderState === 'ONGOING' || orderState === 'CANCELED' || orderState === 'COMPLETED') && (
							<Button
								className="border border-[#E1320F] text-[#E1320F] max-w-[180px] gap-2"
								data-pw="button-cancel-order"
								onClick={onCancelOrder}
							>
								<IconCloseOrange /> Auftrag stornieren
							</Button>
						)}
						{/* Show 'Jetzt bezahlen' only for AWAITING_PAYMENT */}
						{orderState === 'AWAITING_PAYMENT' && (
							<Button
								data-pw="button-pay-order"
								primary
								className="max-w-[180px]"
								onClick={() => Store.navigate('payment')}
							>
								Jetzt bezahlen
							</Button>
						)}
						{orderState === 'COMPLETED' && (
							<Button
								data-pw="button-order-feedback"
								className="border border-[#0063A3] text-[#0063A3] max-w-[240px]"
								onClick={() => setFeedbackOpen(true)}
							>
								Feedback zur Bestellung
							</Button>
						)}
					</div>


				</div>


				{feedbackOpen && (
					<div
						className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4"
						onClick={closeFeedback}
					>
						<div
							data-pw="dialog-order-feedback"
							className="w-full max-w-[520px] rounded-[20px] bg-white p-8 shadow-lg"
							onClick={(e) => e.stopPropagation()}
						>
							<h2 className="text-2xl font-semibold mb-2">Feedback zur Bestellung</h2>
							<p className="text-sm text-[#666] mb-6">{selectedOrder.name}</p>

							<p className="font-semibold mb-2">Wie zufrieden sind Sie?</p>
							<div className="flex gap-1 mb-6">
								{[1, 2, 3, 4, 5].map((n) => (
									<button
										key={n}
										type="button"
										data-pw={`button-feedback-rating-${n}`}
										aria-label={`${n} von 5`}
										className={`text-3xl leading-none ${n <= feedbackRating ? 'text-[#F5A623]' : 'text-[#ccc]'}`}
										onClick={() => setFeedbackRating(n)}
									>
										★
									</button>
								))}
							</div>

							<p className="font-semibold mb-2">Ihre Nachricht</p>
							<textarea
								data-pw="textarea-feedback"
								rows={5}
								maxLength={1000}
								className="w-full rounded-[20px] border border-[#333] px-4 py-3 text-sm outline-none resize-y mb-6"
								placeholder="Was hat gut funktioniert, was können wir verbessern?"
								value={feedbackText}
								onChange={(e) => setFeedbackText(e.target.value)}
							/>

							<div className="flex gap-4">
								<Button
									data-pw="button-feedback-cancel"
									className="border border-[#E1320F] text-[#E1320F]"
									onClick={closeFeedback}
								>
									Abbrechen
								</Button>
								<Button
									data-pw="button-feedback-send"
									primary
									enabled={!feedbackSending && (feedbackText.trim().length > 0 || feedbackRating > 0)}
									onClick={onSendFeedback}
								>
									{feedbackSending ? 'Wird gesendet…' : 'Feedback senden'}
								</Button>
							</div>
						</div>
					</div>
				)}

				<div className="order-page-right max-w-[600px]">
					{/* eslint-disable-next-line react/style-prop-object */}
					<div style={{ aspectRatio: '1 / 1', width: '100%', maxWidth: 600, minWidth: 300 }}>
						<LeafletMap
							data-pw="leaflet-map-order-page"
							coordinates={selectedOrder.coordinates}
							setup={mapSetup}
							view3d={Store.config.view3d}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
